import { useState } from "react";
import { Link } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { ThemeToggle } from "@/design-system/ThemeToggle";
import { FRONTEND_ROUTES } from "@fysaro/shared";
import { useTranslation } from "react-i18next";
import { HelpTrigger } from "@/shared/help/components/HelpTrigger";
import { SURFACE_STYLES } from "@/design-system/lib/surface-styles";
import { cn } from "@/design-system/utils";
import { useAppHref } from "@/utils/appUrl";

export function Header() {
  const { t } = useTranslation(["landing"]);
  const [open, setOpen] = useState(false);
  const homePath = FRONTEND_ROUTES.public.homeClinic;
  const loginHref = useAppHref(FRONTEND_ROUTES.auth.loginClinic);
  const registerHref = useAppHref(FRONTEND_ROUTES.auth.registerClinic);

  return (
    <header className={cn("w-full bg-background sticky top-0 z-50 backdrop-blur-md bg-background/95", SURFACE_STYLES.topbarBorder)} >
      <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
        {/* LOGO */}
        <Link to="/" className="flex items-center gap-2 shrink-0">
          <img
            src="/images/logo_fisio.png"
            alt={t("landing:header.alt_title")}
            className="h-8 w-8"
          />
          <span className="text-xl font-black tracking-tight text-primary">
            {t("landing:header.title")}
          </span>
        </Link>

        {/* NAV DESKTOP */}
        <nav className="hidden md:flex items-center gap-6 text-sm font-medium text-muted-foreground">
          <a href={`${homePath}#hero`} className="hover:text-primary transition">
            {t("landing:header.links.home")}
          </a>
          <a href={`${homePath}#benefits`} className="hover:text-primary transition">
            {t("landing:header.links.features")}
          </a>
          <a
            href={`${homePath}#pricing`}
            className="text-primary hover:opacity-80 transition flex items-center gap-1 font-bold"
          >
            {t("landing:header.links.pricing")}
          </a>

          <Link
            to={FRONTEND_ROUTES.public.homePatient}
            className="text-sm font-semibold hover:text-primary transition"
          >
            {t("landing:header.links.are_you_patient")}
          </Link>

          <span className="h-4 w-px bg-border mx-2" />

          <a
            href={loginHref}
            className="hover:text-primary transition"
          >
            {t("landing:header.links.login")}
          </a>

          <ThemeToggle />
          <HelpTrigger scope="PUBLIC_CLINIC" />
          <a
            href={registerHref}
            className="bg-primary text-white px-4 py-2 rounded-full font-medium hover:bg-primary/80 transition"
          >
            {t("landing:header.main_action")}
          </a>
        </nav>

        {/* MOBILE MENU BUTTON */}
        <button
          className="md:hidden text-primary"
          onClick={() => setOpen(!open)}
        >
          {open ? <X size={28} /> : <Menu size={28} />}
        </button>
      </div>

      {/* MOBILE MENU */}
      {open && (
        <div className="md:hidden bg-card border-t border-border px-6 py-6 space-y-4 animate-in fade-in slide-in-from-top-5 duration-200">
          <a
            href={`${homePath}#hero`}
            className="block text-sm font-medium"
            onClick={() => setOpen(false)}
          >
            {t("landing:header.links.home")}
          </a>
          <a
            href={`${homePath}#benefits`}
            className="block text-sm font-medium"
            onClick={() => setOpen(false)}
          >
            {t("landing:header.links.features")}
          </a>
          <a
            href={`${homePath}#pricing`}
            className="block text-sm font-medium"
            onClick={() => setOpen(false)}
          >
            {t("landing:header.links.pricing")}
          </a>

          <Link
            to={FRONTEND_ROUTES.public.homePatient}
            className="block text-sm font-bold text-primary"
            onClick={() => setOpen(false)}
          >
            {t("landing:header.links.are_you_patient")}
          </Link>

          <hr className="border-border" />

          <a
            href={loginHref}
            className="block text-sm font-medium"
            onClick={() => setOpen(false)}
          >
            {t("landing:header.links.login")}
          </a>

          <ThemeToggle />
          <HelpTrigger scope="PUBLIC_CLINIC" />
          
          <a
            href={registerHref}
            className="block bg-primary text-white px-4 py-2 rounded-full font-medium text-center hover:bg-primary/80 transition"
            onClick={() => setOpen(false)}
          >
            {t("landing:header.main_action")}
          </a>
        </div>
      )}
    </header>
  );
}
